import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Zap, Settings2, Plus, Loader2, Trash2, MoreHorizontal, X, Search, ChevronRight, ChevronDown, Check, Coins, DollarSign, Wallet, Image, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ============================================
// CRYPTO LOGOS (using CoinGecko/public URLs)
// ============================================
const CRYPTO_LOGOS = {
  BTC: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png',
  ETH: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png',
  SOL: 'https://assets.coingecko.com/coins/images/4128/small/solana.png',
  USDC: 'https://assets.coingecko.com/coins/images/6319/small/usdc.png',
  USDT: 'https://assets.coingecko.com/coins/images/325/small/Tether.png',
  BNB: 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png',
  XRP: 'https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png',
  ADA: 'https://assets.coingecko.com/coins/images/975/small/cardano.png',
  DOGE: 'https://assets.coingecko.com/coins/images/5/small/dogecoin.png',
  MATIC: 'https://assets.coingecko.com/coins/images/4713/small/polygon.png',
  DOT: 'https://assets.coingecko.com/coins/images/12171/small/polkadot.png',
  AVAX: 'https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png',
  LINK: 'https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png',
  UNI: 'https://assets.coingecko.com/coins/images/12504/small/uniswap.png',
  ATOM: 'https://assets.coingecko.com/coins/images/1481/small/cosmos_hub.png',
};

const FIAT_FLAGS = {
  USD: '🇺🇸',
  EUR: '🇪🇺',
  GBP: '🇬🇧',
  CAD: '🇨🇦',
  AUD: '🇦🇺',
  JPY: '🇯🇵',
  CHF: '🇨🇭',
};

// ============================================
// TRANSACTION TYPES (comprehensive list)
// ============================================
const TRANSACTION_TYPES = [
  // Liquidity
  { value: 'add_liquidity', label: 'Add Liquidity', group: 'DeFi' },
  { value: 'remove_liquidity', label: 'Remove Liquidity', group: 'DeFi' },
  
  // Basic operations
  { value: 'adjustment', label: 'Adjustment', group: 'Basic' },
  { value: 'airdrop', label: 'Airdrop', group: 'Income' },
  { value: 'asset_migration', label: 'Asset Migration', group: 'Transfer' },
  
  // Borrowing/Lending
  { value: 'borrow', label: 'Borrow', group: 'Lending' },
  { value: 'loan_repayment', label: 'Loan Repayment', group: 'Lending' },
  { value: 'loan_repayment_margin', label: 'Loan Repayment / Margin', group: 'Lending' },
  { value: 'receive_lending_deposit', label: 'Receive Lending Deposit', group: 'Lending' },
  { value: 'receive_lending_withdrawal', label: 'Receive Lending Withdrawal', group: 'Lending' },
  { value: 'send_lending_deposit', label: 'Send Lending Deposit', group: 'Lending' },
  { value: 'send_lending_withdrawal', label: 'Send Lending Withdrawal', group: 'Lending' },
  { value: 'trade_lending_deposit', label: 'Trade Lending Deposit', group: 'Lending' },
  { value: 'trade_lending_withdrawal', label: 'Trade Lending Withdrawal', group: 'Lending' },
  
  // Bridge
  { value: 'bridge', label: 'Bridge', group: 'Transfer' },
  { value: 'receive_bridge', label: 'Receive Bridge', group: 'Transfer' },
  { value: 'send_bridge', label: 'Send Bridge', group: 'Transfer' },
  
  // Trading
  { value: 'buy', label: 'Buy', group: 'Trading' },
  { value: 'sell', label: 'Sell', group: 'Trading' },
  { value: 'trade', label: 'Trade', group: 'Trading' },
  { value: 'linked_trade', label: 'Linked Trade', group: 'Trading' },
  { value: 'close_order', label: 'Close Order', group: 'Trading' },
  { value: 'open_order', label: 'Open Order', group: 'Trading' },
  
  // Deposits/Withdrawals
  { value: 'deposit', label: 'Deposit', group: 'Basic' },
  { value: 'withdrawal', label: 'Withdrawal', group: 'Basic' },
  
  // Transfers
  { value: 'transfer', label: 'Transfer', group: 'Transfer' },
  { value: 'send', label: 'Send', group: 'Transfer' },
  { value: 'receive', label: 'Receive', group: 'Transfer' },
  { value: 'send_gift', label: 'Send Gift', group: 'Transfer' },
  { value: 'receive_gift', label: 'Receive Gift', group: 'Transfer' },
  
  // Fees
  { value: 'fee', label: 'Fee', group: 'Fees' },
  { value: 'fee_blockchain', label: 'Fee / Blockchain Fee', group: 'Fees' },
  { value: 'fee_exchange', label: 'Fee / Exchange Fee', group: 'Fees' },
  { value: 'fee_margin', label: 'Fee / Margin Fee', group: 'Fees' },
  { value: 'fee_storage', label: 'Fee / Storage Deposit', group: 'Fees' },
  
  // Staking
  { value: 'stake', label: 'Stake', group: 'Staking' },
  { value: 'unstake', label: 'Unstake', group: 'Staking' },
  { value: 'staking_reward', label: 'Staking Reward', group: 'Staking' },
  { value: 'collect_rewards', label: 'Collect Rewards', group: 'Staking' },
  { value: 'trade_stake', label: 'Trade Stake', group: 'Staking' },
  { value: 'trade_unstake', label: 'Trade Unstake', group: 'Staking' },
  
  // Margin trading
  { value: 'margin', label: 'Margin', group: 'Margin' },
  { value: 'margin_gain', label: 'Margin Gain', group: 'Margin' },
  { value: 'margin_loss', label: 'Margin Loss', group: 'Margin' },
  { value: 'margin_rebate', label: 'Margin Rebate', group: 'Margin' },
  { value: 'margin_reward', label: 'Margin Reward', group: 'Margin' },
  { value: 'margin_settle', label: 'Margin Settle', group: 'Margin' },
  { value: 'liquidated', label: 'Liquidated', group: 'Margin' },
  
  // Income
  { value: 'mining_reward', label: 'Mining Reward', group: 'Income' },
  { value: 'interest', label: 'Interest (Coinbase type)', group: 'Income' },
  { value: 'interest income', label: 'Interest Income', group: 'Income' },
  { value: 'other_income', label: 'Other Income', group: 'Income' },
  { value: 'rebate', label: 'Rebate', group: 'Income' },
  { value: 'royalty', label: 'Royalty', group: 'Income' },
  { value: 'gambling_income', label: 'Gambling Income', group: 'Income' },
  { value: 'hard_fork', label: 'Hard Fork', group: 'Income' },
  
  // Expenses
  { value: 'interest_expense', label: 'Interest Expense', group: 'Expense' },
  { value: 'gambling_expense', label: 'Gambling Expense', group: 'Expense' },
  { value: 'payment', label: 'Payment', group: 'Expense' },
  { value: 'donation', label: 'Donation', group: 'Expense' },
  { value: 'service', label: 'Service', group: 'Expense' },
  
  // Loss
  { value: 'lost', label: 'Lost', group: 'Loss' },
  { value: 'stolen', label: 'Stolen', group: 'Loss' },
  
  // NFT/Mint
  { value: 'receive_mint', label: 'Receive Mint', group: 'NFT' },
  { value: 'trade_mint', label: 'Trade Mint', group: 'NFT' },
  
  // Wrap/Unwrap
  { value: 'wrap', label: 'Wrap', group: 'DeFi' },
  { value: 'unwrap', label: 'Unwrap', group: 'DeFi' },
  
  // Other
  { value: 'multi_operation', label: 'Multi Operation Transaction', group: 'Other' },
];

// Group transaction types
const groupedTransactionTypes = TRANSACTION_TYPES.reduce((acc, type) => {
  if (!acc[type.group]) acc[type.group] = [];
  acc[type.group].push(type);
  return acc;
}, {});

// ============================================
// ASSET TYPES & OPTIONS (Extended)
// ============================================
const ASSET_TYPES = [
  { value: 'cryptocurrency', label: 'Cryptocurrency', icon: Coins },
  { value: 'fiat', label: 'Fiat Currency', icon: DollarSign },
  { value: 'nft', label: 'NFT', icon: Image },
  { value: 'stablecoin', label: 'Stablecoin', icon: Circle },
  { value: 'defi_token', label: 'DeFi Token', icon: Coins },
  { value: 'blockchain_coin', label: 'Blockchain Coin', icon: Wallet },
];

const CRYPTOCURRENCIES = [
  { value: 'BTC', label: 'Bitcoin', symbol: 'BTC' },
  { value: 'ETH', label: 'Ethereum', symbol: 'ETH' },
  { value: 'SOL', label: 'Solana', symbol: 'SOL' },
  { value: 'BNB', label: 'BNB', symbol: 'BNB' },
  { value: 'XRP', label: 'XRP', symbol: 'XRP' },
  { value: 'ADA', label: 'Cardano', symbol: 'ADA' },
  { value: 'DOGE', label: 'Dogecoin', symbol: 'DOGE' },
  { value: 'MATIC', label: 'Polygon', symbol: 'MATIC' },
  { value: 'DOT', label: 'Polkadot', symbol: 'DOT' },
  { value: 'AVAX', label: 'Avalanche', symbol: 'AVAX' },
  { value: 'LINK', label: 'Chainlink', symbol: 'LINK' },
  { value: 'UNI', label: 'Uniswap', symbol: 'UNI' },
  { value: 'ATOM', label: 'Cosmos', symbol: 'ATOM' },
];

const STABLECOINS = [
  { value: 'USDC', label: 'USD Coin', symbol: 'USDC' },
  { value: 'USDT', label: 'Tether', symbol: 'USDT' },
];

const FIAT_CURRENCIES = [
  { value: 'USD', label: 'US Dollar', symbol: 'USD' },
  { value: 'EUR', label: 'Euro', symbol: 'EUR' },
  { value: 'GBP', label: 'British Pound', symbol: 'GBP' },
  { value: 'CAD', label: 'Canadian Dollar', symbol: 'CAD' },
  { value: 'AUD', label: 'Australian Dollar', symbol: 'AUD' },
  { value: 'JPY', label: 'Japanese Yen', symbol: 'JPY' },
  { value: 'CHF', label: 'Swiss Franc', symbol: 'CHF' },
];

// ============================================
// CONDITION FIELDS
// ============================================
const CONDITION_FIELDS = [
  { value: 'transaction_type', label: 'Transaction Type' },
  { value: 'asset', label: 'Asset' },
  { value: 'asset_type', label: 'Asset Type' },
  { value: 'wallet', label: 'Wallet' },
  { value: 'counterparty', label: 'Counter Party Address' },
];

// ============================================
// ACTION TYPES
// ============================================
const ACTION_TYPES = [
  { value: 'asset_debit', label: 'Asset Debit', description: 'Set debit account for asset' },
  { value: 'asset_credit', label: 'Asset Credit', description: 'Set credit account for asset' },
  { value: 'fee', label: 'Fee', description: 'Set fee account' },
  { value: 'realized', label: 'Realized', description: 'Realized gains/losses account' },
  { value: 'unrealized', label: 'Unrealized', description: 'Unrealized gains/losses account' },
  { value: 'rounding_adjustment', label: 'Rounding Adjustment', description: 'Rounding adjustment account' },
  { value: 'memo', label: 'Memo', description: 'Apply memo text' },
];

// ============================================
// DEFAULT GL ACCOUNTS
// ============================================
const DEFAULT_ACCOUNTS = [
  { code: '1000', name: 'Cash' },
  { code: '1100', name: 'Bank Account' },
  { code: '1200', name: 'Crypto Assets' },
  { code: '1210', name: 'Crypto Assets - BTC' },
  { code: '1220', name: 'Crypto Assets - ETH' },
  { code: '1230', name: 'Crypto Assets - SOL' },
  { code: '1240', name: 'Crypto Assets - USDC' },
  { code: '4000', name: 'Crypto Income' },
  { code: '4100', name: 'Staking Income' },
  { code: '4200', name: 'Airdrop Income' },
  { code: '4300', name: 'Interest Income' },
  { code: '5000', name: 'Crypto Payments' },
  { code: '5100', name: 'Transaction Fees' },
  { code: '5200', name: 'Exchange Fees' },
  { code: '6000', name: 'Marketing - VAs' },
  { code: '6100', name: 'Contractor Payments' },
  { code: '6200', name: 'Service Expenses' },
  { code: '7100', name: 'Realized Gains' },
  { code: '7200', name: 'Realized Losses' },
  { code: '7300', name: 'Unrealized Gains' },
  { code: '7400', name: 'Unrealized Losses' },
  { code: '8000', name: 'Rounding Adjustment' },
];

// All assets combined for action selects
const ALL_ASSETS = [
  ...CRYPTOCURRENCIES.map(c => ({ ...c, type: 'crypto' })),
  ...STABLECOINS.map(s => ({ ...s, type: 'stablecoin' })),
  ...FIAT_CURRENCIES.map(f => ({ ...f, type: 'fiat' })),
];

// ============================================
// HIERARCHICAL ASSET DROPDOWN COMPONENT
// ============================================
function AssetHierarchicalDropdown({ value, onChange, placeholder = "Select asset..." }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(['cryptocurrencies']);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const filterItems = (items) => {
    if (!searchQuery) return items;
    return items.filter(item => 
      item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.symbol.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const filteredCryptos = filterItems(CRYPTOCURRENCIES);
  const filteredStablecoins = filterItems(STABLECOINS);
  const filteredFiat = filterItems(FIAT_CURRENCIES);

  const getSelectedLabel = () => {
    if (!value) return placeholder;
    const allItems = [...CRYPTOCURRENCIES, ...STABLECOINS, ...FIAT_CURRENCIES];
    const found = allItems.find(item => item.value === value);
    return found ? `${found.symbol} - ${found.label}` : value;
  };

  const renderAssetItem = (item, type) => {
    const isSelected = value === item.value;
    const logo = CRYPTO_LOGOS[item.symbol];
    const flag = FIAT_FLAGS[item.symbol];

    return (
      <div
        key={item.value}
        onClick={() => {
          onChange(item.value);
          setIsOpen(false);
          setSearchQuery('');
        }}
        className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-700/50 rounded-md mx-1 ${
          isSelected ? 'bg-blue-600/20' : ''
        }`}
      >
        {/* Logo or Flag */}
        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
          {logo ? (
            <img src={logo} alt={item.symbol} className="w-5 h-5 rounded-full" />
          ) : flag ? (
            <span className="text-lg">{flag}</span>
          ) : (
            <div className="w-5 h-5 rounded-full bg-zinc-600 flex items-center justify-center text-xs text-zinc-300">
              {item.symbol.charAt(0)}
            </div>
          )}
        </div>
        
        {/* Name and Symbol */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white font-medium">{item.label}</span>
            <span className="text-zinc-500 text-sm">({item.symbol})</span>
          </div>
        </div>
        
        {/* Checkmark if selected */}
        {isSelected && (
          <Check className="w-4 h-4 text-blue-400 flex-shrink-0" />
        )}
      </div>
    );
  };

  const categories = [
    {
      id: 'asset_types',
      label: 'Asset Types',
      icon: ChevronRight,
      count: ASSET_TYPES.length,
      isExpandable: false,
      items: ASSET_TYPES,
    },
    {
      id: 'cryptocurrencies',
      label: 'Cryptocurrencies',
      icon: Coins,
      count: CRYPTOCURRENCIES.length,
      isExpandable: true,
      items: filteredCryptos,
      type: 'crypto',
    },
    {
      id: 'stablecoins',
      label: 'Stablecoins',
      icon: Circle,
      count: STABLECOINS.length,
      isExpandable: true,
      items: filteredStablecoins,
      type: 'stablecoin',
    },
    {
      id: 'fiat',
      label: 'Fiat Currencies',
      icon: DollarSign,
      count: FIAT_CURRENCIES.length,
      isExpandable: true,
      items: filteredFiat,
      type: 'fiat',
    },
  ];

  return (
    <div className="relative flex-1" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-left hover:border-zinc-600 transition-colors"
      >
        <span className={value ? 'text-white' : 'text-zinc-500'}>
          {getSelectedLabel()}
        </span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full min-w-[320px] bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-zinc-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-700 rounded-md text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-zinc-600"
                autoFocus
              />
            </div>
          </div>

          {/* Categories */}
          <div className="max-h-80 overflow-y-auto py-1">
            {categories.map((category) => {
              const isExpanded = expandedCategories.includes(category.id);
              const Icon = category.icon;
              const hasResults = category.items.length > 0;

              if (searchQuery && !hasResults && category.isExpandable) return null;

              return (
                <div key={category.id}>
                  {/* Category Header */}
                  <div
                    onClick={() => category.isExpandable && toggleCategory(category.id)}
                    className={`flex items-center gap-2 px-3 py-2 ${
                      category.isExpandable 
                        ? 'cursor-pointer hover:bg-zinc-700/30' 
                        : 'cursor-default'
                    } ${
                      category.id === 'asset_types' ? 'bg-blue-600/20' : ''
                    }`}
                  >
                    {category.isExpandable ? (
                      isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-zinc-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-zinc-400" />
                      )
                    ) : (
                      <ChevronRight className="w-4 h-4 text-blue-400" />
                    )}
                    
                    <Icon className={`w-4 h-4 ${category.id === 'asset_types' ? 'text-blue-400' : 'text-zinc-400'}`} />
                    
                    <span className={`font-medium ${category.id === 'asset_types' ? 'text-blue-400' : 'text-white'}`}>
                      {category.label}
                    </span>
                    
                    <span className="text-zinc-500 text-sm ml-auto">
                      {category.items.length} items
                    </span>
                  </div>

                  {/* Category Items (only for expandable categories) */}
                  {category.isExpandable && isExpanded && (
                    <div className="pl-4 pb-1">
                      {category.items.map(item => renderAssetItem(item, category.type))}
                      {category.items.length === 0 && (
                        <div className="px-3 py-2 text-zinc-500 text-sm">No results</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// TRANSACTION TYPE HIERARCHICAL DROPDOWN
// ============================================
function TransactionTypeDropdown({ value, onChange, placeholder = "Select transaction type..." }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(['Trading', 'Transfer']);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleGroup = (group) => {
    setExpandedGroups(prev => 
      prev.includes(group) 
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };

  const getSelectedLabel = () => {
    if (!value) return placeholder;
    const found = TRANSACTION_TYPES.find(t => t.value === value);
    return found ? found.label : value;
  };

  const filteredGroups = Object.entries(groupedTransactionTypes).reduce((acc, [group, types]) => {
    if (!searchQuery) {
      acc[group] = types;
    } else {
      const filtered = types.filter(t => 
        t.label.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (filtered.length > 0) acc[group] = filtered;
    }
    return acc;
  }, {});

  const GROUP_ICONS = {
    'DeFi': Coins,
    'Basic': Circle,
    'Income': DollarSign,
    'Transfer': Wallet,
    'Lending': DollarSign,
    'Trading': Coins,
    'Fees': Circle,
    'Staking': Coins,
    'Margin': Coins,
    'Expense': DollarSign,
    'Loss': Circle,
    'NFT': Image,
    'Other': Circle,
  };

  return (
    <div className="relative flex-1" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-left hover:border-zinc-600 transition-colors"
      >
        <span className={value ? 'text-white' : 'text-zinc-500'}>
          {getSelectedLabel()}
        </span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full min-w-[320px] bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-zinc-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                placeholder="Search transaction types..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-700 rounded-md text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-zinc-600"
                autoFocus
              />
            </div>
          </div>

          {/* Groups */}
          <div className="max-h-80 overflow-y-auto py-1">
            {Object.entries(filteredGroups).map(([group, types]) => {
              const isExpanded = expandedGroups.includes(group);
              const Icon = GROUP_ICONS[group] || Circle;

              return (
                <div key={group}>
                  <div
                    onClick={() => toggleGroup(group)}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-zinc-700/30"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-zinc-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-zinc-400" />
                    )}
                    <Icon className="w-4 h-4 text-zinc-400" />
                    <span className="text-white font-medium">{group}</span>
                    <span className="text-zinc-500 text-sm ml-auto">{types.length} items</span>
                  </div>

                  {isExpanded && (
                    <div className="pl-4 pb-1">
                      {types.map(type => (
                        <div
                          key={type.value}
                          onClick={() => {
                            onChange(type.value);
                            setIsOpen(false);
                            setSearchQuery('');
                          }}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-700/50 rounded-md mx-1 ${
                            value === type.value ? 'bg-blue-600/20' : ''
                          }`}
                        >
                          <span className="text-white">{type.label}</span>
                          {value === type.value && (
                            <Check className="w-4 h-4 text-blue-400 ml-auto" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// ACCOUNT HIERARCHICAL DROPDOWN (Chart of Accounts)
// ============================================
function AccountHierarchicalDropdown({ value, onChange, accounts = [], placeholder = "Select account..." }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedClasses, setExpandedClasses] = useState(['EXPENSE', 'ASSET']);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleClass = (cls) => {
    setExpandedClasses(prev => 
      prev.includes(cls) 
        ? prev.filter(c => c !== cls)
        : [...prev, cls]
    );
  };

  // Account classes with colors
  const ACCOUNT_CLASSES = [
    { id: 'ASSET', label: 'Assets', color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
    { id: 'LIABILITY', label: 'Liabilities', color: 'text-red-400', bgColor: 'bg-red-500/10' },
    { id: 'EQUITY', label: 'Equity', color: 'text-purple-400', bgColor: 'bg-purple-500/10' },
    { id: 'REVENUE', label: 'Revenue', color: 'text-green-400', bgColor: 'bg-green-500/10' },
    { id: 'EXPENSE', label: 'Expenses', color: 'text-orange-400', bgColor: 'bg-orange-500/10' },
  ];

  const getSelectedAccount = () => {
    if (!value) return null;
    return accounts.find(a => a.code === value) || DEFAULT_ACCOUNTS.find(a => a.code === value);
  };

  const selectedAccount = getSelectedAccount();

  // Filter and group accounts
  const filteredAccounts = accounts.filter(acc => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (acc.code || '').toLowerCase().includes(query) ||
           (acc.name || '').toLowerCase().includes(query);
  });

  const groupedAccounts = ACCOUNT_CLASSES.map(cls => ({
    ...cls,
    accounts: filteredAccounts.filter(acc => acc.class === cls.id)
  })).filter(group => group.accounts.length > 0);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-left hover:border-zinc-600 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {selectedAccount ? (
            <>
              <span className="font-mono text-zinc-400 text-sm">{selectedAccount.code}</span>
              <span className="text-white truncate">{selectedAccount.name}</span>
            </>
          ) : (
            <span className="text-zinc-400">{placeholder}</span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl max-h-80 overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-zinc-800">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search accounts..."
                className="w-full pl-9 pr-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm focus:outline-none focus:border-zinc-600"
                autoFocus
              />
            </div>
          </div>

          {/* Account List */}
          <div className="overflow-y-auto max-h-64">
            {groupedAccounts.length === 0 ? (
              <div className="px-3 py-4 text-center text-zinc-500 text-sm">
                No accounts found
              </div>
            ) : (
              groupedAccounts.map(group => {
                const isExpanded = expandedClasses.includes(group.id);
                
                return (
                  <div key={group.id} className="border-b border-zinc-800/50 last:border-0">
                    {/* Class Header */}
                    <div
                      onClick={() => toggleClass(group.id)}
                      className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-zinc-800/50 ${group.bgColor}`}
                    >
                      {isExpanded ? (
                        <ChevronDown className={`w-4 h-4 ${group.color}`} />
                      ) : (
                        <ChevronRight className={`w-4 h-4 ${group.color}`} />
                      )}
                      <span className={`font-medium ${group.color}`}>{group.label}</span>
                      <span className="text-zinc-500 text-sm ml-auto">({group.accounts.length})</span>
                    </div>

                    {/* Accounts */}
                    {isExpanded && (
                      <div className="pb-1">
                        {group.accounts.map(acc => (
                          <div
                            key={acc.code}
                            onClick={() => {
                              onChange(acc.code);
                              setIsOpen(false);
                              setSearchQuery('');
                            }}
                            className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-zinc-700/50 mx-1 rounded-md ${
                              value === acc.code ? 'bg-blue-600/20' : ''
                            }`}
                          >
                            <span className="font-mono text-zinc-400 text-sm w-12">{acc.code}</span>
                            <span className="text-white truncate">{acc.name}</span>
                            {acc.xero_account_id && (
                              <span className="ml-auto px-1.5 py-0.5 text-[10px] rounded bg-[#13B5EA]/20 text-[#13B5EA]">
                                Xero
                              </span>
                            )}
                            {value === acc.code && (
                              <Check className="w-4 h-4 text-blue-400 ml-auto" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CointrackerRules() {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);

  const [ruleForm, setRuleForm] = useState({
    name: '',
    condition_logic: 'and', // 'and' or 'or'
    conditions: [{ field: 'transaction_type', operator: 'is', value: '', asset_type: '', asset_value: '' }],
    actions: [{ type: 'asset_debit', asset: '', account_code: '', memo: '' }],
  });

  useEffect(() => {
    fetchRules();
    fetchWallets();
    fetchAccounts();
  }, []);

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cointracker/rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWallets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cointracker/wallets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWallets(response.data.wallets || []);
    } catch (error) {
      console.error('Failed to fetch wallets:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cointracker/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fetchedAccounts = response.data.accounts || [];
      
      if (fetchedAccounts.length > 0) {
        // Use only Xero/imported accounts
        setAccounts(fetchedAccounts.sort((a, b) => (a.code || '').localeCompare(b.code || '')));
      } else {
        // Fallback to defaults only if no accounts imported
        setAccounts(DEFAULT_ACCOUNTS.map(acc => ({
          ...acc,
          class: acc.code.startsWith('1') ? 'ASSET' :
                 acc.code.startsWith('4') ? 'REVENUE' :
                 acc.code.startsWith('5') || acc.code.startsWith('6') ? 'EXPENSE' :
                 acc.code.startsWith('7') ? 'EQUITY' : 'EXPENSE'
        })));
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      // Use default accounts if fetch fails
      setAccounts(DEFAULT_ACCOUNTS.map(acc => ({
        ...acc,
        class: acc.code.startsWith('1') ? 'ASSET' :
               acc.code.startsWith('4') ? 'REVENUE' :
               acc.code.startsWith('5') || acc.code.startsWith('6') ? 'EXPENSE' :
               acc.code.startsWith('7') ? 'EQUITY' : 'EXPENSE'
      })));
    }
  };

  const handleCreateRule = async () => {
    if (!ruleForm.name.trim()) {
      toast.error('Please enter a rule name');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingRule) {
        const response = await axios.put(`${API_URL}/cointracker/rules/${editingRule.rule_id}`, ruleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const reapplied = response.data.reapplied_count || 0;
        if (reapplied > 0) {
          toast.success(`Rule updated! Applied to ${reapplied} journal entries`);
        } else {
          toast.success('Rule updated');
        }
      } else {
        const response = await axios.post(`${API_URL}/cointracker/rules`, ruleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const applied = response.data.applied_count || 0;
        if (applied > 0) {
          toast.success(`Rule created! Applied to ${applied} journal entries`);
        } else {
          toast.success('Rule created');
        }
      }
      
      setShowCreateDialog(false);
      setEditingRule(null);
      resetForm();
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/cointracker/rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleArchiveRule = async (ruleId, archived) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/cointracker/rules/${ruleId}`, 
        { archived: !archived },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(archived ? 'Rule restored' : 'Rule archived');
      fetchRules();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name,
      condition_logic: rule.condition_logic || 'and',
      conditions: rule.conditions || [{ field: 'transaction_type', operator: 'is', value: '' }],
      actions: rule.actions || [{ type: 'asset_debit', asset: '', account_code: '', memo: '' }],
    });
    setShowCreateDialog(true);
  };

  const resetForm = () => {
    setRuleForm({
      name: '',
      condition_logic: 'and',
      conditions: [{ field: 'transaction_type', operator: 'is', value: '', asset_type: '', asset_value: '' }],
      actions: [{ type: 'asset_debit', asset: '', account_code: '', memo: '' }],
    });
  };

  // Toggle condition logic between AND and OR
  const toggleConditionLogic = () => {
    setRuleForm(prev => ({
      ...prev,
      condition_logic: prev.condition_logic === 'and' ? 'or' : 'and'
    }));
  };

  // Condition helpers
  const addCondition = () => {
    setRuleForm(prev => ({
      ...prev,
      conditions: [...prev.conditions, { field: 'transaction_type', operator: 'is', value: '', asset_type: '', asset_value: '' }]
    }));
  };

  const removeCondition = (index) => {
    if (ruleForm.conditions.length <= 1) return;
    setRuleForm(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const updateCondition = (index, key, value) => {
    setRuleForm(prev => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => {
        if (i !== index) return c;
        const updated = { ...c, [key]: value };
        // Reset dependent fields
        if (key === 'field') {
          updated.value = '';
          updated.asset_type = '';
          updated.asset_value = '';
        }
        if (key === 'asset_type') {
          updated.asset_value = '';
        }
        return updated;
      })
    }));
  };

  // Action helpers
  const addAction = () => {
    setRuleForm(prev => ({
      ...prev,
      actions: [...prev.actions, { type: 'asset_debit', asset: '', account_code: '', memo: '' }]
    }));
  };

  const removeAction = (index) => {
    if (ruleForm.actions.length <= 1) return;
    setRuleForm(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };

  const updateAction = (index, key, value) => {
    setRuleForm(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) => {
        if (i !== index) return a;
        const updated = { ...a, [key]: value };
        // Reset dependent fields when type changes
        if (key === 'type') {
          updated.asset = '';
          updated.account_code = '';
          updated.memo = '';
        }
        return updated;
      })
    }));
  };

  // Display helpers
  const formatConditionDisplay = (condition) => {
    const field = CONDITION_FIELDS.find(f => f.value === condition.field);
    let valueLabel = condition.value;
    
    if (condition.field === 'wallet') {
      const wallet = wallets.find(w => w.wallet_id === condition.value);
      valueLabel = wallet ? wallet.name : condition.value;
    } else if (condition.field === 'transaction_type') {
      const type = TRANSACTION_TYPES.find(t => t.value === condition.value);
      valueLabel = type ? type.label : condition.value;
    } else if (condition.field === 'asset') {
      const allAssets = [...CRYPTOCURRENCIES, ...STABLECOINS, ...FIAT_CURRENCIES];
      const asset = allAssets.find(a => a.value === (condition.asset_value || condition.value));
      if (asset) {
        valueLabel = `${asset.symbol} - ${asset.label}`;
      } else if (condition.asset_type && condition.asset_value) {
        valueLabel = `${condition.asset_type}: ${condition.asset_value}`;
      }
    } else if (condition.field === 'asset_type') {
      const assetType = ASSET_TYPES.find(a => a.value === condition.value);
      valueLabel = assetType ? assetType.label : condition.value;
    }
    
    return { field: field?.label || condition.field, value: valueLabel };
  };

  const formatActionDisplay = (action) => {
    const actionType = ACTION_TYPES.find(a => a.value === action.type);
    const account = accounts.find(a => a.code === action.account_code) || DEFAULT_ACCOUNTS.find(a => a.code === action.account_code);
    
    if (action.type === 'memo') {
      return `Apply Memo → "${action.memo}"`;
    }
    
    let display = actionType?.label || action.type;
    if (action.asset) {
      const allAssets = [...CRYPTOCURRENCIES, ...STABLECOINS, ...FIAT_CURRENCIES];
      const asset = allAssets.find(a => a.value === action.asset);
      display += asset ? ` (${asset.symbol})` : ` (${action.asset})`;
    }
    if (account) display += ` → ${account.name}`;
    
    return display;
  };

  const filteredRules = rules.filter(r => activeTab === 'archive' ? r.archived : !r.archived);

  // Render condition value selector based on field type
  const renderConditionValueSelector = (condition, index) => {
    switch (condition.field) {
      case 'transaction_type':
        return (
          <TransactionTypeDropdown
            value={condition.value}
            onChange={(value) => updateCondition(index, 'value', value)}
            placeholder="Select type"
          />
        );

      case 'asset':
        return (
          <AssetHierarchicalDropdown
            value={condition.asset_value || condition.value}
            onChange={(value) => {
              updateCondition(index, 'asset_value', value);
              updateCondition(index, 'value', value);
            }}
            placeholder="Select asset"
          />
        );

      case 'asset_type':
        return (
          <Select
            value={condition.value}
            onValueChange={(value) => updateCondition(index, 'value', value)}
          >
            <SelectTrigger className="flex-1 bg-zinc-800 border-zinc-700 text-white">
              <SelectValue placeholder="Select asset type" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              {ASSET_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <SelectItem key={type.value} value={type.value} className="text-white">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-zinc-400" />
                      {type.label}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        );

      case 'wallet':
        return (
          <Select
            value={condition.value}
            onValueChange={(value) => updateCondition(index, 'value', value)}
          >
            <SelectTrigger className="flex-1 bg-zinc-800 border-zinc-700 text-white">
              <SelectValue placeholder="Select wallet" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              {wallets.map((wallet) => (
                <SelectItem key={wallet.wallet_id} value={wallet.wallet_id} className="text-white">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-zinc-400" />
                    {wallet.name} ({wallet.address.slice(0, 8)}...)
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'counterparty':
        return (
          <Input
            placeholder="Enter address..."
            value={condition.value}
            onChange={(e) => updateCondition(index, 'value', e.target.value)}
            className="flex-1 bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
          />
        );

      default:
        return null;
    }
  };

  // Render action fields based on type
  const renderActionFields = (action, index) => {
    const needsAssetSelect = ['asset_debit', 'asset_credit'].includes(action.type);
    const needsAccountSelect = ['asset_debit', 'asset_credit', 'fee', 'realized', 'unrealized', 'rounding_adjustment'].includes(action.type);
    const needsMemo = action.type === 'memo';

    return (
      <div className="flex items-center gap-2 flex-1">
        {needsAssetSelect && (
          <div className="w-44">
            <AssetHierarchicalDropdown
              value={action.asset}
              onChange={(value) => updateAction(index, 'asset', value)}
              placeholder="Select asset"
            />
          </div>
        )}
        
        {needsAccountSelect && (
          <div className="flex-1">
            <AccountHierarchicalDropdown
              value={action.account_code}
              onChange={(value) => updateAction(index, 'account_code', value)}
              accounts={accounts}
              placeholder="Select account..."
            />
          </div>
        )}
        
        {needsMemo && (
          <Input
            placeholder="Enter memo text..."
            value={action.memo || ''}
            onChange={(e) => updateAction(index, 'memo', e.target.value)}
            className="flex-1 bg-zinc-800 border-zinc-700 text-white"
          />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Rules</h2>
          <p className="text-sm text-zinc-400 mt-1">
            Manage your active automation rules. The rule with the smaller number has the highest priority.
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setEditingRule(null);
            setShowCreateDialog(true);
          }}
          className="bg-white text-black hover:bg-zinc-200"
          data-testid="create-rule-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create a rule
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-zinc-800">
        <button
          onClick={() => setActiveTab('active')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'active'
              ? 'text-white border-white'
              : 'text-zinc-500 border-transparent hover:text-zinc-300'
          }`}
        >
          Active ({rules.filter(r => !r.archived).length})
        </button>
        <button
          onClick={() => setActiveTab('archive')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'archive'
              ? 'text-white border-white'
              : 'text-zinc-500 border-transparent hover:text-zinc-300'
          }`}
        >
          Archive ({rules.filter(r => r.archived).length})
        </button>
      </div>

      {/* Rules List */}
      {filteredRules.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12">
            <div className="text-center">
              <Settings2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                {activeTab === 'archive' ? 'No archived rules' : 'No rules yet'}
              </h3>
              <p className="text-zinc-400 text-sm mb-4">
                {activeTab === 'archive' 
                  ? 'Archived rules will appear here'
                  : 'Create rules to automatically categorize your transactions'}
              </p>
              {activeTab === 'active' && (
                <Button 
                  onClick={() => { resetForm(); setShowCreateDialog(true); }} 
                  variant="outline" 
                  className="border-zinc-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Rule
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredRules.map((rule, index) => (
            <Card key={rule.rule_id} className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-zinc-800 text-zinc-400 text-sm font-medium">
                      {index + 1}
                    </div>
                    
                    <div className="space-y-3">
                      <h3 className="text-white font-medium">{rule.name}</h3>
                      
                      {/* Conditions */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Zap className="w-4 h-4 text-yellow-500" />
                          <span className="text-zinc-400">When</span>
                          {(rule.conditions || []).length > 1 && (
                            <span className={`px-1.5 py-0.5 text-xs rounded ${
                              rule.condition_logic === 'or' 
                                ? 'bg-orange-500/20 text-orange-400' 
                                : 'bg-blue-500/20 text-blue-400'
                            }`}>
                              {(rule.condition_logic || 'and').toUpperCase()} logic
                            </span>
                          )}
                        </div>
                        <div className="ml-6 space-y-1">
                          {(rule.conditions || []).map((condition, cIdx) => {
                            const display = formatConditionDisplay(condition);
                            const logic = rule.condition_logic || 'and';
                            return (
                              <div key={cIdx} className="flex items-center gap-2 text-sm">
                                {cIdx > 0 && (
                                  <span className={`px-1.5 py-0.5 text-xs rounded mr-1 ${
                                    logic === 'or' 
                                      ? 'bg-orange-500/20 text-orange-400' 
                                      : 'bg-blue-500/20 text-blue-400'
                                  }`}>
                                    {logic.toUpperCase()}
                                  </span>
                                )}
                                <span className="text-zinc-400">{display.field}</span>
                                <span className="text-zinc-600">is</span>
                                <span className="px-2 py-0.5 bg-zinc-800 rounded text-white">{display.value || '-'}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Settings2 className="w-4 h-4 text-orange-500" />
                          <span className="text-zinc-400">Then</span>
                        </div>
                        <div className="ml-6 space-y-1">
                          {(rule.actions || []).map((action, aIdx) => (
                            <div key={aIdx} className="text-sm text-zinc-300">
                              {formatActionDisplay(action)}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs text-zinc-600">
                        {rule.times_applied > 0 && <span>Applied {rule.times_applied} times</span>}
                        <span>Created {new Date(rule.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                        <MoreHorizontal className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-zinc-800 border-zinc-700">
                      <DropdownMenuItem onClick={() => handleEditRule(rule)} className="text-white hover:bg-zinc-700 cursor-pointer">
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleArchiveRule(rule.rule_id, rule.archived)} className="text-white hover:bg-zinc-700 cursor-pointer">
                        {rule.archived ? 'Restore' : 'Archive'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDeleteRule(rule.rule_id)} className="text-red-400 hover:bg-zinc-700 cursor-pointer">
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingRule ? 'Edit Rule' : 'Create a Rule'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Add a transaction automation rule
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-white">Description</label>
              <p className="text-xs text-zinc-500">Pick a descriptive name which explains what this rule does</p>
              <Input
                placeholder="What does this rule do?"
                value={ruleForm.name}
                onChange={(e) => setRuleForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="rule-name-input"
              />
            </div>

            {/* Conditions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-500" />
                <label className="text-sm font-medium text-white">When (Conditions)</label>
              </div>
              <p className="text-xs text-zinc-500">Define when this rule should trigger</p>
              
              {ruleForm.conditions.map((condition, index) => (
                <div key={index} className="space-y-2">
                  {index > 0 && (
                    <div className="flex items-center gap-2 ml-2">
                      <button
                        type="button"
                        onClick={toggleConditionLogic}
                        className={`px-2 py-0.5 text-xs rounded cursor-pointer hover:opacity-80 transition-all ${
                          ruleForm.condition_logic === 'or'
                            ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
                            : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                        }`}
                        title="Click to toggle between AND/OR"
                      >
                        {ruleForm.condition_logic.toUpperCase()}
                      </button>
                      <span className="text-xs text-zinc-600">(click to toggle)</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Select
                      value={condition.field}
                      onValueChange={(value) => updateCondition(index, 'field', value)}
                    >
                      <SelectTrigger className="w-44 bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue placeholder="Select field" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {CONDITION_FIELDS.map((field) => (
                          <SelectItem key={field.value} value={field.value} className="text-white">
                            {field.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <span className="text-zinc-500 text-sm">is</span>
                    
                    {renderConditionValueSelector(condition, index)}
                    
                    {ruleForm.conditions.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCondition(index)}
                        className="text-zinc-400 hover:text-red-400 p-1"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              
              <Button
                variant="outline"
                size="sm"
                onClick={addCondition}
                className="border-zinc-700 text-zinc-400 hover:text-white"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Condition
              </Button>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-orange-500" />
                <label className="text-sm font-medium text-white">Then (Actions)</label>
              </div>
              <p className="text-xs text-zinc-500">Define what should happen when conditions are met</p>
              
              {ruleForm.actions.map((action, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Select
                    value={action.type}
                    onValueChange={(value) => updateAction(index, 'type', value)}
                  >
                    <SelectTrigger className="w-48 bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Select action" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {ACTION_TYPES.map((actionType) => (
                        <SelectItem key={actionType.value} value={actionType.value} className="text-white">
                          {actionType.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {renderActionFields(action, index)}
                  
                  {ruleForm.actions.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAction(index)}
                      className="text-zinc-400 hover:text-red-400 p-1"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              
              <Button
                variant="outline"
                size="sm"
                onClick={addAction}
                className="border-zinc-700 text-zinc-400 hover:text-white"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Action
              </Button>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowCreateDialog(false); setEditingRule(null); resetForm(); }}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateRule}
              disabled={saving}
              className="bg-white text-black hover:bg-zinc-200"
              data-testid="save-rule-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingRule ? 'Save Changes' : 'Create and Enable Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
